import React, { useMemo } from "react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { Button } from "@/components/Button/Button";
import DataTable from "@/components/Datatable/Datatable";
import { Column } from "@/components/Datatable/types";
import { Specialty } from "@/modules/administration/features/EmployeeDirectoryManagement/api/Specialty/fetchSpecialties/types";
import { useSpecialtyManagementPage } from "@/modules/administration/features/EmployeeDirectoryManagement/hooks/useSpecialtyManagementPage";
import CreateSpecialtyDrawer from "@/modules/administration/features/EmployeeDirectoryManagement/components/Specialty/CreateSpecialtyDrawer/CreateSpecialtyDrawer";
import EditSpecialtyDrawer from "@/modules/administration/features/EmployeeDirectoryManagement/components/Specialty/EditSpecialtyDrawer/EditSpecialtyDrawer";
import DeleteSpecialtyConfirmation from "@/modules/administration/features/EmployeeDirectoryManagement/components/Specialty/DeleteSpecialtyConfirmation/DeleteSpecialtyConfirmation";
import SpecialtyActionMenu from "@/modules/administration/features/EmployeeDirectoryManagement/components/Specialty/SpecialtyActionMenu/SpecialtyActionMenu";

const SpecialtyManagementPage: React.FC = () => {
  const {
    list,
    loading,
    error,
    loadingCreate,
    loadingUpdate,
    showCreateDrawer,
    editTarget,
    deleteTarget,
    openCreateDrawer,
    closeCreateDrawer,
    openEditDrawer,
    closeEditDrawer,
    openDeleteModal,
    closeDeleteModal,
    handleCreate,
    handleUpdate,
    handleDelete,
  } = useSpecialtyManagementPage();

  const columns = useMemo<Column<Specialty>[]>(
    () => [
      {
        id: "name",
        header: "Name",
        accessorKey: "name",
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <SpecialtyActionMenu
            onDelete={openDeleteModal}
            onEdit={openEditDrawer}
            specialty={row.original}
          />
        ),
      },
    ],
    [openEditDrawer, openDeleteModal],
  );

  return (
    <>
      <MainPageWrapper
        actions={<Button onClick={openCreateDrawer}>Create Specialty</Button>}
        error={error}
        title="Employee Directory — Specialties"
      >
        <DataTable
          columns={columns}
          data={list}
          error={error}
          loading={loading}
          noDataMessage="No specialties yet."
          onPageChange={() => undefined}
          pageIndex={0}
          pageSize={list.length || 10}
          rowKeyExtractor={(item) => item.uuid}
          totalCount={list.length}
        />
      </MainPageWrapper>

      <CreateSpecialtyDrawer
        isOpen={showCreateDrawer}
        loading={loadingCreate}
        onClose={closeCreateDrawer}
        onSubmit={handleCreate}
      />

      <EditSpecialtyDrawer
        loading={loadingUpdate}
        onClose={closeEditDrawer}
        onSubmit={handleUpdate}
        specialty={editTarget}
      />

      <DeleteSpecialtyConfirmation
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        specialty={deleteTarget}
      />
    </>
  );
};

export default SpecialtyManagementPage;
